/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  DeepDiveTemplate,
  type DeepDiveConfig,
} from "@/components/landing/deep-dive-template";
import {
  Square,
  Ruler,
  MousePointer2,
  Box,
  Pencil,
  Keyboard,
  Layers,
  Upload,
} from "lucide-react";

const config: DeepDiveConfig = {
  parent: { label: "Takeoffs", href: "/features/takeoffs" },
  badge: "Measurement Tool",
  title: "Four measurement types.",
  titleHighlight: "One canvas. Every unit a contractor needs.",
  description:
    "Most takeoffs come down to four things: area, linear, count, and volume. Measure all four in one tool and keep everything tied to the same estimate.",

  painPoint: {
    title: "Why separate tools for each measurement type is a problem",
    description:
      "Switching between disconnected measurement tools causes mistakes, re-entry, and slow estimate production.",
    bullets: [
      "Area, linear, count, and volume in the same tool — no mode-switching gymnastics",
      "Every measurement is stored as editable geometry, not a static number",
      "Quick Entry panel for when typing is faster than drawing",
      "Measurements organize into work areas as you go, so structure carries into the estimate",
      "Cross-page volume measurements: draw the area on the floor plan, carry the height from the section",
    ],
  },

  walkthrough: {
    title: "How to use the measurement tool",
    steps: [
      {
        title: "Pick a type and draw",
        description:
          "Choose area, linear, count, or volume, then draw directly on the plan. Quantities update while you work so you can validate on the spot.",
        details: [
          "Area: click corners of a polygon — square footage updates live",
          "Linear: click along a path — running linear feet displayed as you go",
          "Count: click to place each marker — running count tallied",
          "Volume: draw an area polygon, enter height — cubic yards calculated",
          "Undo the last point with one key, cancel the measurement with Escape",
        ],
      },
      {
        title: "Name, assign, and organize",
        description:
          "Name and assign each measurement to the right work area so your populated estimate stays organized and readable.",
        details: [
          "Name each measurement for clarity in the estimate",
          "Assign to a work area (front yard, Level 2, exterior, etc.)",
          "Optional phase assignment for sequenced jobs",
          "Measurements sidebar shows every measurement with its work area",
          "Click to select, rename, or reassign",
        ],
      },
      {
        title: "Edit any time — geometry is stored, not snapshots",
        description:
          "Need to revise? Edit measurements directly instead of deleting and redrawing from scratch.",
        details: [
          "Select any measurement to edit its geometry",
          "Move, add, or delete individual points",
          "Quantity recalculates live on every edit",
          "Rename, reassign work area, or convert area to volume (and back)",
          "Full undo/redo support",
        ],
      },
      {
        title: "Quick Entry — skip the canvas when typing is faster",
        description:
          "When quantities are already known, use Quick Entry to add them directly and keep moving.",
        details: [
          "Type quantity + unit + description directly",
          "Assign to a work area from the panel",
          "Mixed workflow: some drawn, some typed, same sidebar",
          "Works alongside canvas measurements seamlessly",
        ],
      },
    ],
  },

  details: {
    title: "Measurement specifics",
    items: [
      {
        icon: Square,
        title: "Area — SF or SY",
        description:
          "Click around a room, patio, lot, or roof. Supports cutouts using dedicated cut tools (line, shape, or circle). Output in square feet or square yards.",
      },
      {
        icon: Ruler,
        title: "Linear — LF",
        description:
          "Trace walls, curbs, pipe runs, fencing, or trim. Multi-segment polylines that sum to total linear feet as you draw each segment.",
      },
      {
        icon: MousePointer2,
        title: "Count — EA",
        description:
          "Click each fixture, outlet, door, tree, sprinkler head, or rooftop unit. Running count tallied live. Markers visible on the canvas.",
      },
      {
        icon: Box,
        title: "Volume — CY",
        description:
          "Draw an area polygon, enter a height. Output in cubic yards. Cross-page support: draw the area on the floor plan, carry the height from the section drawing.",
      },
      {
        icon: Pencil,
        title: "Stored Geometry",
        description:
          "Measurements remain editable so you can adjust scope quickly as plans evolve.",
      },
      {
        icon: Keyboard,
        title: "Quick Entry Panel",
        description:
          "Type measurements directly when you know the numbers. No canvas interaction needed. The panel creates the same measurement records as drawing does.",
      },
    ],
  },

  siblings: [
    {
      label: "Blueprint Editor",
      href: "/features/takeoffs/blueprint-editor",
      icon: Upload,
      description: "Upload PDFs, navigate plan sets, zoom and pan",
    },
    {
      label: "Plan Sets",
      href: "/features/takeoffs/plan-sets",
      icon: Layers,
      description: "Multi-page sets with cross-page measurements",
    },
  ],
};

export function MeasurementToolPageClient() {
  return <DeepDiveTemplate config={config} />;
}
